/* ============================================================
   Health & wellness calculator math (pure functions).
   All core functions work in metric base units (kg, cm); the UI converts
   from imperial before calling them.
   ============================================================ */

export const LB_TO_KG = 0.45359237
export const IN_TO_CM = 2.54

export const kgFromLb = (lb) => lb * LB_TO_KG
export const lbFromKg = (kg) => kg / LB_TO_KG
export const cmFromIn = (inch) => inch * IN_TO_CM
export const inFromCm = (cm) => cm / IN_TO_CM
export const cmFromFtIn = (ft, inch) => (ft * 12 + inch) * IN_TO_CM

export const round = (n, d = 1) => {
  if (!Number.isFinite(n)) return null
  const f = 10 ** d
  return Math.round(n * f) / f
}

/* ---------------- BMI ---------------- */
export function bmi(kg, cm) {
  if (!(kg > 0) || !(cm > 0)) return null
  const m = cm / 100
  return kg / (m * m)
}

export function bmiCategory(value) {
  if (value == null) return null
  if (value < 18.5) return { label: 'Underweight', tone: 'amber' }
  if (value < 25) return { label: 'Normal weight', tone: 'green' }
  if (value < 30) return { label: 'Overweight', tone: 'amber' }
  return { label: 'Obese', tone: 'red' }
}

// Healthy weight range (kg) for a given height, BMI 18.5–24.9
export function healthyWeightRange(cm) {
  if (!(cm > 0)) return null
  const m = cm / 100
  return [18.5 * m * m, 24.9 * m * m]
}

/* ---------------- BMR (Mifflin-St Jeor) ---------------- */
export function bmr({ sex, kg, cm, age }) {
  if (!(kg > 0) || !(cm > 0) || !(age > 0)) return null
  const base = 10 * kg + 6.25 * cm - 5 * age
  return sex === 'female' ? base - 161 : base + 5
}

/* ---------------- TDEE ---------------- */
export const ACTIVITY_LEVELS = [
  { id: 'sedentary', label: 'Sedentary — little or no exercise', factor: 1.2 },
  { id: 'light', label: 'Lightly active — 1–3 days/week', factor: 1.375 },
  { id: 'moderate', label: 'Moderately active — 3–5 days/week', factor: 1.55 },
  { id: 'active', label: 'Very active — 6–7 days/week', factor: 1.725 },
  { id: 'athlete', label: 'Extra active — hard training / physical job', factor: 1.9 },
]

export function tdee(bmrValue, factor) {
  if (!(bmrValue > 0) || !(factor > 0)) return null
  return bmrValue * factor
}

/* ---------------- Body fat (US Navy circumference) ----------------
   Formula uses inches + log base 10. UI passes cm; we convert. */
const log10 = (x) => Math.log10(x)

export function bodyFatNavy({ sex, cm, neckCm, waistCm, hipCm }) {
  const height = inFromCm(cm)
  const neck = inFromCm(neckCm)
  const waist = inFromCm(waistCm)
  const hip = inFromCm(hipCm)
  if (!(height > 0) || !(neck > 0) || !(waist > 0)) return null

  let bf
  if (sex === 'female') {
    if (!(hip > 0)) return null
    if (waist + hip - neck <= 0) return null
    bf = 163.205 * log10(waist + hip - neck) - 97.684 * log10(height) - 78.387
  } else {
    if (waist - neck <= 0) return null
    bf = 86.010 * log10(waist - neck) - 70.041 * log10(height) + 36.76
  }
  if (!Number.isFinite(bf) || bf <= 0) return null
  return Math.min(bf, 75)
}

export function bodyFatCategory(bf, sex) {
  if (bf == null) return null
  const t = sex === 'female'
    ? [[13, 'Essential fat', 'blue'], [20, 'Athletes', 'green'], [24, 'Fitness', 'green'], [31, 'Average', 'amber'], [Infinity, 'Above average', 'red']]
    : [[5, 'Essential fat', 'blue'], [13, 'Athletes', 'green'], [17, 'Fitness', 'green'], [24, 'Average', 'amber'], [Infinity, 'Above average', 'red']]
  for (const [max, label, tone] of t) if (bf <= max) return { label, tone }
  return null
}

/* ---------------- Calories burned (MET) ---------------- */
export const MET_ACTIVITIES = [
  { id: 'walking', label: 'Walking (3.5 mph)', met: 4.3 },
  { id: 'running', label: 'Running (6 mph)', met: 9.8 },
  { id: 'cycling', label: 'Cycling (moderate)', met: 7.5 },
  { id: 'swimming', label: 'Swimming (moderate)', met: 5.8 },
  { id: 'weights', label: 'Weight training', met: 3.5 },
  { id: 'hiit', label: 'HIIT / vigorous circuit', met: 8.0 },
  { id: 'elliptical', label: 'Elliptical trainer', met: 5.0 },
  { id: 'rowing', label: 'Rowing (moderate)', met: 7.0 },
  { id: 'yoga', label: 'Yoga', met: 2.5 },
  { id: 'basketball', label: 'Basketball', met: 6.5 },
  { id: 'hiking', label: 'Hiking', met: 6.0 },
  { id: 'jumprope', label: 'Jump rope', met: 11.0 },
]

// kcal = MET * 3.5 * kg / 200 per minute
export function caloriesBurned({ met, kg, minutes }) {
  if (!(met > 0) || !(kg > 0) || !(minutes > 0)) return null
  return ((met * 3.5 * kg) / 200) * minutes
}
